import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TextField } from '@mui/material';
import { LocationOn, Search } from '@mui/icons-material';
import { MapComponentProps, MapPickerProps } from '../types/type';
import { useApiKey } from '../hooks/useApiKey';

const MapComponent: React.FC<MapComponentProps> = ({ center, zoom, onLocationSelect }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [marker, setMarker] = useState<google.maps.Marker>();
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder>();

  useEffect(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      
      const newMarker = new window.google.maps.Marker({
        position: center,
        map: newMap,
        draggable: true,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#FF0000"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 40),
        }
      });

      const newGeocoder = new window.google.maps.Geocoder();
      
      setMap(newMap);
      setMarker(newMarker);
      setGeocoder(newGeocoder);
    }
  }, [ref, map, center, zoom]);

  useEffect(() => {
    if (map) {
      const clickListener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
        if (e.latLng && marker) {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          
          marker.setPosition({ lat, lng });
          
          if (geocoder) {
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              if (status === 'OK' && results && results[0]) {
                onLocationSelect(lat, lng, results[0].formatted_address);
              } else {
                onLocationSelect(lat, lng);
              }
            });
          } else {
            onLocationSelect(lat, lng);
          }
        }
      });

      return () => {
        google.maps.event.removeListener(clickListener);
      };
    }
  }, [map, marker, geocoder, onLocationSelect]);

  useEffect(() => {
    if (marker) {
      const dragEndListener = marker.addListener('dragend', (e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          
          if (geocoder) {
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              if (status === 'OK' && results && results[0]) {
                onLocationSelect(lat, lng, results[0].formatted_address);
              } else {
                onLocationSelect(lat, lng);
              }
            });
          } else {
            onLocationSelect(lat, lng);
          }
        }
      });

      return () => {
        google.maps.event.removeListener(dragEndListener);
      };
    }
  }, [marker, geocoder, onLocationSelect]);

  return <div ref={ref} style={{ width: '100%', height: '400px' }} />;
};

const render = (status: Status): React.ReactElement => {
  switch (status) {
    case Status.LOADING:
      return <Typography>Loading map...</Typography>;
    case Status.FAILURE:
      return <Typography color="error">Error loading map</Typography>;
    case Status.SUCCESS:
      return <React.Fragment />; 
    default:
      return <React.Fragment />;
  }
};

const MapPicker: React.FC<MapPickerProps> = ({ 
  open, 
  onClose, 
  onLocationSelect, 
  initialLat = 14.599513,
  initialLng = 120.984234
}) => {
  const { apiKeyValue, loading } = useApiKey();
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
    address?: string;
  } | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const handleLocationSelect = useCallback((lat: number, lng: number, address?: string) => {
    setSelectedLocation({ lat, lng, address });
  }, []);

  const handleConfirm = () => {
    if (selectedLocation) {
      onLocationSelect(selectedLocation.lat, selectedLocation.lng, selectedLocation.address);
      onClose();
    }
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) return;
    
    setIsSearching(true);
    const geocoder = new window.google.maps.Geocoder();
    
    geocoder.geocode({ address: searchValue }, (results, status) => {
      setIsSearching(false);
      if (status === 'OK' && results && results[0] && results[0].geometry) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        setSelectedLocation({ lat, lng, address: results[0].formatted_address });
      } else {
        alert('The address is not found.');
      }
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '70vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOn color="primary" />
          Choose an address on map
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {/* Search Box */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search address..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isSearching}
              startIcon={<Search />}
              sx={{paddingX: 3}}
            >
              Search
            </Button>
          </Box>
        </Box>

        {/* Selected Location Info */}
        {selectedLocation && (
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Location:
            </Typography>
            <Typography variant="body2">
              <strong>Coordinates:</strong> {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
            </Typography>
            {selectedLocation.address && (
              <Typography variant="body2">
                <strong>Address:</strong> {selectedLocation.address}
              </Typography>
            )}
          </Box>
        )}

        {/* Map */}
        <Box sx={{ height: 'calc(100% - 300px)' }}>
          <Wrapper 
            apiKey={apiKeyValue || ''} 
            render={render}
            libraries={['geometry', 'places']}
          >
            <MapComponent
              center={{ lat: selectedLocation?.lat || initialLat, lng: selectedLocation?.lng || initialLng }}
              zoom={15}
              onLocationSelect={handleLocationSelect}
            />
          </Wrapper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="primary"
          disabled={!selectedLocation}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapPicker;